/**
 * Logger utility that only logs in development mode
 * In production, all log calls are no-ops to improve performance
 */

// Check for development environment in both browser (Vite) and Node.js contexts
// We use a safe check to avoid ReferenceErrors in different environments
const getIsDevelopment = () => {
  try {
    // Check Node.js environment
    // @ts-ignore - process might not be defined in all environments
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV) {
      // @ts-ignore
      return process.env.NODE_ENV !== 'production'
    }

    // Check Vite/Browser environment
    // @ts-ignore - import.meta is available in ESM environments
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      // @ts-ignore
      return import.meta.env.DEV
    }
  } catch (_e) {
    // Fallback if something goes wrong
    return false
  }

  return false
}

let isDevelopment = getIsDevelopment()

// Export for testing purposes
export const setLoggerDevelopmentMode = (mode: boolean) => {
  isDevelopment = mode
}

/**
 * Logger object that conditionally logs based on environment
 */
export const logger = {
  /**
   * Logs a message to the console (development only)
   */
  log: (...args: unknown[]) => {
    if (isDevelopment) {
      console.log(...args)
    }
  },

  /**
   * Logs a warning to the console (development only)
   */
  warn: (...args: unknown[]) => {
    if (isDevelopment) {
      console.warn(...args)
    }
  },

  /**
   * Logs an error to the console (always logs errors, even in production)
   * This is useful for critical errors that need to be tracked
   */
  error: (...args: unknown[]) => {
    // Errors are always logged, even in production
    console.error(...args)
  },

  /**
   * Logs an info message to the console (development only)
   */
  info: (...args: unknown[]) => {
    if (isDevelopment) {
      console.info(...args)
    }
  },

  /**
   * Logs a debug message to the console (development only)
   */
  debug: (...args: unknown[]) => {
    if (isDevelopment) {
      console.debug(...args)
    }
  },

  /**
   * Logs a table to the console (development only)
   */
  table: (data: unknown) => {
    if (isDevelopment) {
      console.table(data)
    }
  },

  /**
   * Groups console logs (development only)
   */
  group: (label: string) => {
    if (isDevelopment) {
      console.group(label)
    }
  },

  /**
   * Ends a console group (development only)
   */
  groupEnd: () => {
    if (isDevelopment) {
      console.groupEnd()
    }
  },
}

export default logger
